import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Switch,
  FormControlLabel,
  Snackbar,
  Alert,
  CircularProgress,
  Link
} from '@mui/material';
import axios from 'axios';
import { Link as RouterLink } from 'react-router-dom';
import { useReseller } from '../../contexts/ResellerContext';
import { getToolCatalogByProviderKey } from '../../common/toolCatalog';
import AgentMcpToolInstructionsModal from './AgentMcpToolInstructionsModal';
import { cardStyle, cardsContainerStyle, configureButtonStyle } from './integrationCardStyles';

function parseConnectionIds(connectionId) {
  if (!connectionId) return [];
  return String(connectionId)
    .split(',')
    .map((s) => parseInt(s.trim(), 10))
    .filter((n) => !isNaN(n) && n > 0);
}

export default function ToolsIntegrations({ agentId }) {
  const { reseller } = useReseller();
  const productName = reseller?.productName || 'Chattitude';
  const catalogByKey = useMemo(
    () => getToolCatalogByProviderKey(productName, reseller),
    [productName, reseller]
  );

  const [mcpConnections, setMcpConnections] = useState([]);
  const [attachedIds, setAttachedIds] = useState(() => new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [configureConn, setConfigureConn] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const loadData = useCallback(async () => {
    if (!agentId) {
      setMcpConnections([]);
      setAttachedIds(new Set());
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const [mcpResp, agentResp] = await Promise.all([
        axios.get('/integrations/mcp/connected'),
        axios.post('/agents/getAgentDetail', null, { params: { agentId } })
      ]);
      setMcpConnections(mcpResp.data || []);
      setAttachedIds(new Set(parseConnectionIds(agentResp.data?.connectionId)));
    } catch (err) {
      console.error('ToolsIntegrations loadData', err?.response?.status, err?.response?.data, err);
      setMcpConnections([]);
      setAttachedIds(new Set());
      setSnackbar({ open: true, message: 'Failed to load tool integrations', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [agentId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleToggleAttach = async (connectionIdNum, nextAttached) => {
    const next = new Set(attachedIds);
    if (nextAttached) {
      next.add(connectionIdNum);
    } else {
      next.delete(connectionIdNum);
    }
    const sorted = [...next].sort((a, b) => a - b);
    const connectionIdStr = sorted.length ? sorted.join(',') : null;
    setSaving(true);
    try {
      await axios.post('/agents/updateToolConnections', {
        agentId,
        connectionId: connectionIdStr
      });
      setAttachedIds(next);
      setSnackbar({ open: true, message: 'Saved', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to save tool attachments', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" sx={{ mb: 1 }}>Tools Integrations</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Attach MCP tools that are already connected for your tenant to this agent. Use{' '}
        <Link component={RouterLink} to="/tools">Tools &amp; Integrations</Link>
        {' '}to connect tenant credentials first.
      </Typography>

      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
          <CircularProgress />
        </Box>
      ) : mcpConnections.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No MCP tools are connected yet. Connect integrations under{' '}
          <Link component={RouterLink} to="/tools">Tools &amp; Integrations</Link>.
        </Typography>
      ) : (
        <div style={cardsContainerStyle}>
          {mcpConnections.map((conn) => {
            const meta = catalogByKey.get(conn.providerKey);
            const title = meta?.name || conn.displayName || conn.providerKey || 'Tool';
            const description = meta?.description || `${conn.displayName || conn.providerKey} integration.`;
            const logo = meta?.logo;
            const logoFallback = meta?.logoFallback;
            const attached = attachedIds.has(conn.id);

            return (
              <div key={conn.id} style={cardStyle}>
                <div style={{ fontWeight: 600, fontSize: 20, marginBottom: 12, textAlign: 'center' }}>{title}</div>
                {logo ? (
                  <img
                    src={logo}
                    alt=""
                    style={{ width: 64, height: 64, marginBottom: 16, objectFit: 'contain' }}
                    onError={(e) => {
                      if (logoFallback) {
                        e.target.onerror = null;
                        e.target.src = logoFallback;
                      }
                    }}
                  />
                ) : (
                  <div style={{ width: 64, height: 64, marginBottom: 16, background: '#f0f0f0', borderRadius: 8 }} />
                )}
                <div style={{ fontSize: 15, color: '#555', textAlign: 'center', marginBottom: 16, minHeight: 48 }}>
                  {description}
                </div>
                <FormControlLabel
                  sx={{ mb: 1 }}
                  control={(
                    <Switch
                      color="primary"
                      checked={attached}
                      disabled={saving}
                      onChange={() => handleToggleAttach(conn.id, !attached)}
                    />
                  )}
                  label="Attach to agent"
                />
                <button
                  type="button"
                  onClick={() => setConfigureConn(conn)}
                  style={configureButtonStyle}
                >
                  Configure
                </button>
              </div>
            );
          })}
        </div>
      )}

      <AgentMcpToolInstructionsModal
        open={configureConn != null}
        onClose={() => setConfigureConn(null)}
        agentId={agentId}
        connection={configureConn}
        attached={configureConn ? attachedIds.has(configureConn.id) : false}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          variant="filled" sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
